import { ANALYTICS_COOKIE_PATTERN, CONSENT_COOKIE_LIFETIME_DAYS, CONSENT_COOKIE_NAME } from "./constants";
import type { CookieConsentType } from "./types";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return entry === undefined ? undefined : decodeURIComponent(entry.slice(prefix.length));
}

/**
 * The stored choice; nothing when the visitor has not chosen yet or the cookie cannot be read
 */
export function readConsent(): CookieConsentType | undefined {
  const value = readCookie(CONSENT_COOKIE_NAME);

  if (!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as Partial<CookieConsentType> | null;
    return parsed && typeof parsed === "object"
      ? { strictly_necessary: true, analytics: parsed.analytics === true }
      : undefined;
  } catch {
    return undefined;
  }
}

export function writeConsent(consent: CookieConsentType, now = new Date()): void {
  const expires = new Date(now.getTime() + CONSENT_COOKIE_LIFETIME_DAYS * DAY_IN_MS).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(consent))}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

/**
 * Deletes a cookie whatever domain it was set for: this host and every parent domain (Google Analytics sets its
 * cookies on the top-level domain it can, e.g. ".aiodp.ai")
 */
function deleteCookie(name: string): void {
  const expired = `${name}=; Max-Age=0; path=/`;
  const labels = window.location.hostname.split(".");

  document.cookie = expired;

  for (let i = 0; i < labels.length - 1; i++) {
    document.cookie = `${expired}; domain=${labels.slice(i).join(".")}`;
  }
}

/**
 * Deletes the cookies Google Analytics already set, once the visitor no longer allows them
 */
export function deleteAnalyticsCookies(): void {
  document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter((name) => ANALYTICS_COOKIE_PATTERN.test(name))
    .forEach(deleteCookie);
}
